import utilsService from "api/utils/utils.service";
import { ILogDownloadRequest } from "api/utils/utils.type";
import { RasterFormat } from "./constants";
import { isIosDevice, openInNewTab } from "./platform";
// Imported as well as re-exported: this module still calls it (the format re-encoders below).
import { getFilenameFromUrl } from "./downloadName";
import { isExternalImageUrl } from "utils/imageOrigin";
import { isPaywallRejection } from "utils/paywallRejection";
import { httpStatusOf } from "utils/httpStatusOf";

// Re-export so existing callers keep importing from `components/DownloadButton/utils`.
export { isIosDevice, openInNewTab };
// Naming moved to ./downloadName for the same reason ./platform exists — 22 files import
// these two from here, and this module cannot be imported by a naming unit test because
// `pingDownloadLog` pulls in the HttpClient/Redux/lottie tree.
export { getFilenameFromUrl, isSvgUrl, sanitizeFilename } from "./downloadName";

/**
 * REF-5664: fire-and-forget backoffice download log for client-side downloads that do NOT go
 * through useDownloadLogic (the bespoke `document.createElement("a")` / `downloadFile` sites spread
 * across the app: photoshoot zips, bulk process, marketplace, history panels, svg editor, etc.).
 * Best-effort — never throws, never blocks. Derives file_type from the URL when not supplied.
 * Do NOT add this inside downloadFile/downloadImageInFormat itself — the DownloadButton /
 * useNodeActions paths already log with richer context and would double-count.
 */
export const pingDownloadLog = (
  url: string | undefined,
  opts: { action?: ILogDownloadRequest["action"]; surface?: string; designId?: string; imageId?: string; fileType?: string; itemCount?: number } = {}
): void => {
  if (!url) return;
  const ext = (url.split("?")[0].split("#")[0].split(".").pop() || "").toLowerCase();
  const fileType = opts.fileType || (ext && ext.length <= 4 ? ext : undefined);
  void utilsService.logDownload({
    action: opts.action || "download_image",
    file_type: fileType,
    image_url: url,
    surface: opts.surface,
    design_id: opts.designId || undefined,
    image_id: opts.imageId || undefined,
    item_count: opts.itemCount,
  });
};

const FORMAT_MIME: Record<string, string> = {
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  png: "image/png",
  webp: "image/webp",
};

/**
 * Download `url` re-encoded to the requested image format. Plain `downloadFile`
 * saves the source bytes untouched and `getFilenameFromUrl` keeps the URL's
 * original extension, so "Download as JPG" used to hand back the source PNG.
 * Re-encoding through a canvas guarantees the bytes (and the extension) match
 * the chosen format.
 */
export const downloadImageAsFormat = async (url: string, format: RasterFormat, filenameBase?: string): Promise<void> => {
  if (!url) {
    throw new Error("Download URL is required");
  }

  // iOS/iPadOS: the canvas re-encode below finishes after an `await`, so the
  // `<a download>` click that follows is dropped (no user activation) and
  // nothing saves (REF-5796). Open the source image in a new tab so the user
  // can save it; the client-side format conversion is skipped on iOS.
  if (isIosDevice()) {
    openInNewTab(url);

    return;
  }

  const mime = FORMAT_MIME[format] || "image/png";
  const base = (filenameBase || getFilenameFromUrl(url, format)).replace(/\.[^/.]+$/, "");
  const filename = `${base}.${format}`;

  // Fetch as a blob and load via an object URL so the <img> stays same-origin —
  // drawing a cross-origin <img> straight to a canvas taints it and makes
  // toDataURL throw a SecurityError.
  const response = await fetch(url);

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  const blob = await response.blob();

  if (blob.size === 0) {
    throw new Error("Downloaded file is empty");
  }

  const objectUrl = URL.createObjectURL(blob);

  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const image = new Image();
      image.onload = () => resolve(image);
      image.onerror = () => reject(new Error("Failed to load image for conversion"));
      image.src = objectUrl;
    });

    const canvas = document.createElement("canvas");
    canvas.width = img.naturalWidth;
    canvas.height = img.naturalHeight;

    const ctx = canvas.getContext("2d");
    if (!ctx) {
      throw new Error("Canvas 2D context unavailable");
    }

    // JPEG has no alpha channel — flatten transparency onto white so PNGs with
    // transparent regions don't export with a black background.
    if (mime === "image/jpeg") {
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
    }

    ctx.drawImage(img, 0, 0);

    const dataUrl = canvas.toDataURL(mime, 0.95);

    const link = document.createElement("a");
    link.style.display = "none";
    link.href = dataUrl;
    link.download = filename;

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
};

export const downloadFile = async (url: string, filename: string): Promise<void> => {
  if (!url) {
    throw new Error("Download URL is required");
  }

  if (!filename) {
    throw new Error("Filename is required");
  }

  // iOS/iPadOS: a programmatic `<a download>` click after the async fetch below
  // is ignored (the user-activation window has closed) and no download ever
  // starts (REF-5796). Open the image in a new tab so the user can save it. This
  // runs synchronously within the click handler's activation window.
  if (isIosDevice()) {
    openInNewTab(url);

    return;
  }

  try {
    const response = await fetch(url);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const blob = await response.blob();

    if (blob.size === 0) {
      throw new Error("Downloaded file is empty");
    }

    const objectUrl = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.style.display = "none";
    link.href = objectUrl;
    link.download = filename;

    document.body.appendChild(link);
    link.click();

    // Clean up
    URL.revokeObjectURL(objectUrl);
    document.body.removeChild(link);
  } catch (error) {
    console.error("Error downloading file", { status: httpStatusOf(error) });

    if (error instanceof TypeError && error.message.includes("fetch")) {
      throw new Error("Network error occurred while downloading");
    }

    throw error;
  }
};

/**
 * Image format parsed from a URL's final path segment (lowercased, jpeg→jpg
 * normalized). Null when the URL has no recognizable extension (e.g. a signed
 * URL with no suffix, or a data: URL).
 */
const getUrlImageFormat = (url: string): RasterFormat | null => {
  const ext = url.match(/\.([a-z0-9]+)(?:[?#].*)?$/i)?.[1]?.toLowerCase();

  if (ext === "png") return "png";
  if (ext === "jpg" || ext === "jpeg") return "jpg";
  if (ext === "webp") return "webp";

  return null;
};

/**
 * Download `url` saved as `format`, re-encoding ONLY when a real conversion is
 * needed. When the source is already that format the original bytes are copied
 * untouched — avoiding the lossy JPEG recompression, PNG metadata stripping, and
 * transparency-to-white flattening that an unconditional canvas re-encode
 * (downloadImageAsFormat) applies. When the source format can't be determined it
 * re-encodes, so the saved bytes + extension still match the requested format.
 *
 * `filenameBase` (optional) is the user-chosen base name (no extension); when
 * provided the extension is always derived from `format` so switching PNG↔JPG
 * can't corrupt the name.
 */
export const downloadImageInFormat = async (url: string, format: RasterFormat, filenameBase?: string): Promise<void> => {
  if (getUrlImageFormat(url) === format) {
    const base = filenameBase || getFilenameFromUrl(url, format).replace(/\.[^/.]+$/, "");
    await downloadFile(url, `${base}.${format}`);

    return;
  }

  await downloadImageAsFormat(url, format, filenameBase);
};

/**
 * One-click image download for the bespoke sites that bypass `useDownloadLogic` (Discover, the
 * Trend Agent, the Trends panel, moodboard detail). Their images are often third-party
 * (`i.pinimg.com`) with no `Access-Control-Allow-Origin`, so the browser `fetch` inside
 * `downloadFile` rejects with `TypeError: Failed to fetch` while the image is on screen
 * (REF-6494; Sentry 7678520752 was that rejection escaping a `void downloadFile(...)`).
 *
 * Direct first — lossless for CORS-friendly hosts, and `downloadFile`'s iOS open-in-tab branch runs
 * before any await, inside the click's activation window (REF-5796). When that fails for an
 * external origin, the BE fetches and re-hosts the bytes via `utils/process-download`. A failure on
 * our own buckets is a real failure and is not retried.
 *
 * Resolves `true` when the file was saved, `false` when the interceptor already ran its own UX
 * (`_handled`, or the 402 paywall). Rejects on a real failure — callers must catch it and show an
 * error; never `void` it.
 */
export const downloadImageWithServerFallback = async (url: string, filename: string): Promise<boolean> => {
  try {
    await downloadFile(url, filename);

    return true;
  } catch (error) {
    if (!isExternalImageUrl(url)) throw error;
  }

  let res: any;
  try {
    res = await utilsService.processDownload({ image_url: url, file_type: getUrlImageFormat(filename) ?? "jpg", ai_label: false });
  } catch (error) {
    // 402 rejects, but the paywall snackbar is already dispatched — don't add a second error.
    if (isPaywallRejection(error)) return false;
    throw error;
  }

  // [HTTP_HANDLED_RESPONSE]: 401/403/409/503 resolve with _handled — the interceptor did the UX.
  if (res?._handled) return false;

  const processedUrl = res?.responseData?.download_url ?? res?.download_url;
  if (!processedUrl) throw new Error("process-download returned no URL");

  // Save under the SOURCE name: `processedUrl` is an opaque `processed_download/<uuid>` object.
  await downloadFile(processedUrl, filename);

  return true;
};
